import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { useState } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useError } from '@/modules/utils/custom-hooks/use-error';

import { useDocumentStatusApi } from '../../hooks/use-document-status-api';
import type { StatusDataProps, StatusStateProps } from '../components/types';

export const useStatusParams = ({
	clearStatusState,
	handleStatusState,
}: {
	clearStatusState: () => void;
	handleStatusState: (data: Partial<StatusStateProps>) => void;
}) => {
	const t = useTranslations();
	const { showToast } = useAppContext();
	const [statusData, setStatusData] = useState<Record<string, StatusDataProps>>({});
	const { setError, error } = useError();
	const { deleteDocumentStatus, updateDocumentStatus } = useDocumentStatusApi();
	const { isPending } = deleteDocumentStatus;

	const handleSaveButton = (status: Data.Entity<'api::project-status.project-status'>) => {
		const { name, color } = statusData[status.documentId];
		updateDocumentStatus
			.mutateAsync({
				documentId: status.documentId,
				body: {
					name,
					color,
				},
			})
			.then(clearStatusState)
			.catch((error) => {
				setError({ message: error.response.data.error.message, details: {} });
			});
	};
	const handleEditButton = (status: Data.Entity<'api::project-status.project-status'>) => {
		handleStatusState({ currentId: status.documentId });
		setStatusData((prevData) => ({
			...prevData,
			[status.documentId]: {
				name: status.name ?? prevData[status.documentId]?.name,
				color: status.color ?? prevData[status.documentId]?.color,
			},
		}));
		setError({ message: null, details: {} });
	};
	const handleStatusChange = ({ documentId, newColor, newName }: { documentId: string; newName?: string; newColor?: string }) => {
		setStatusData((prevData) => ({
			...prevData,
			[documentId]: {
				name: newName ?? prevData[documentId]?.name,
				color: newColor ?? prevData[documentId]?.color,
			},
		}));
		setError({ message: null, details: {} });
	};
	const handleDeleteButton = (documentId: string) => {
		deleteDocumentStatus
			.mutateAsync({ documentId })
			.then(clearStatusState)
			.catch((error) => showToast({ message: t(error.response.data.error.message), variant: 'error' }));
	};

	const handleCancelButton = (status: Data.Entity<'api::project-status.project-status'>) => {
		clearStatusState();
		setStatusData((prevData) => ({
			...prevData,
			[status.documentId]: {
				name: status.name ?? prevData[status.documentId]?.name,
				color: status.color ?? prevData[status.documentId]?.color,
			},
		}));
	};

	return {
		t,
		statusData,
		error,
		isPending,
		handleSaveButton,
		handleEditButton,
		handleStatusChange,
		handleDeleteButton,
		handleCancelButton,
	};
};
